import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Button, Form, Input, Modal, Table, Space, notification, Popconfirm, Tag, Tooltip, Upload, Switch, Select, InputNumber, Tour, ConfigProvider } from 'antd';
import type { TourProps } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, UploadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { CustomizationService } from '../services/CustomizationService';
import { Customization } from '../types/customization';
import { DishService } from '../services/dishService';
import { Dish } from '../types/dish';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import { parseExcelToCustomizations, downloadCustomizationExcelTemplate } from '../utils/customizationExcelUtils';
import viVN from 'antd/locale/vi_VN';

const { Search } = Input;
const customizationService = new CustomizationService();
const dishService = new DishService();

const CACHE_TTL = 60 * 1000;

type CacheEntry<T> = {
  data: T[];
  timestamp: number;
};

let customizationsCache: CacheEntry<Customization> | null = null;
let customizationsRequest: Promise<Customization[]> | null = null;
let dishesCache: CacheEntry<Dish> | null = null;
let dishesRequest: Promise<Dish[]> | null = null;

const isCacheValid = <T,>(cache: CacheEntry<T> | null) =>
  !!cache && Date.now() - cache.timestamp < CACHE_TTL;

const CustomizationManagement: React.FC = () => {
  const isMountedRef = useRef(true);
  const [customizations, setCustomizations] = useState<Customization[]>([]);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCustomization, setEditingCustomization] = useState<Customization | null>(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [isTourOpen, setIsTourOpen] = useState(false);

  // Tour refs
  const titleRef = useRef(null);
  const searchRef = useRef(null);
  const addButtonRef = useRef(null);
  const uploadButtonRef = useRef(null);
  const tableRef = useRef(null);

  // Tour steps
  const tourSteps: TourProps['steps'] = [
    {
      title: 'Quản lý tùy chỉnh',
      description: 'Chào mừng bạn đến với trang Quản lý tùy chỉnh! Đây là nơi bạn có thể tạo và quản lý các tùy chỉnh cho món ăn, giúp khách hàng có thể chọn các phụ kiện, topping hoặc tùy chọn đi kèm.',
      target: () => titleRef.current,
    },
    {
      title: 'Tìm kiếm tùy chỉnh',
      description: 'Sử dụng thanh tìm kiếm này để tìm tùy chỉnh theo tên hoặc mã món áp dụng.',
      target: () => searchRef.current,
    },
    {
      title: 'Thêm tùy chỉnh mới',
      description: 'Nhấn vào đây để tạo một tùy chỉnh mới. Bạn sẽ cần nhập tên, thành phố, mã món áp dụng và các nhóm tùy chọn.',
      target: () => addButtonRef.current,
    },
    {
      title: 'Tải lên file Excel',
      description: 'Sử dụng tính năng này để cập nhật hoặc thêm mới nhiều tùy chỉnh cùng lúc thông qua file Excel, giúp tiết kiệm thời gian khi có nhiều tùy chỉnh cần quản lý.',
      target: () => uploadButtonRef.current,
    },
    {
      title: 'Bảng danh sách tùy chỉnh',
      description: 'Bảng hiển thị tất cả các tùy chỉnh đã tạo, bao gồm tên, thành phố, mã món áp dụng và các nhóm tùy chọn. Bạn có thể chỉnh sửa hoặc xóa tùy chỉnh từ đây.',
      target: () => tableRef.current,
    },
    {
      title: 'Kết thúc hướng dẫn!',
      description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn có thể tạo và quản lý các tùy chỉnh để nâng cao trải nghiệm đặt món của khách hàng.',
      target: () => titleRef.current,
    },
  ];

  const updateCustomizationsState = useCallback((updater: (prev: Customization[]) => Customization[]) => {
    setCustomizations((prev) => {
      const next = updater(prev);
      customizationsCache = {
        data: next,
        timestamp: Date.now(),
      };
      return next;
    });
  }, []);

  const fetchCustomizations = useCallback(async (force = false): Promise<Customization[]> => {
    if (!force && isCacheValid(customizationsCache)) {
      if (isMountedRef.current) {
        setCustomizations(customizationsCache!.data);
      }
      return customizationsCache!.data;
    }

    if (customizationsRequest) {
      const sharedData = await customizationsRequest;
      if (isMountedRef.current) {
        setCustomizations(sharedData);
      }
      return sharedData;
    }

    if (isMountedRef.current) {
      setLoading(true);
    }
    customizationsRequest = customizationService.getAllCustomizations();

    try {
      const fetchedCustomizations = await customizationsRequest;
      customizationsCache = {
        data: fetchedCustomizations,
        timestamp: Date.now(),
      };
      if (isMountedRef.current) {
        setCustomizations(fetchedCustomizations);
      }
      return fetchedCustomizations;
    } catch (error) {
      console.error('Error fetching customizations:', error);
      if (isMountedRef.current) {
        notification.error({
          message: 'Lỗi',
          description: 'Không thể tải danh sách customization.',
        });
      }
      return [];
    } finally {
      customizationsRequest = null;
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const fetchDishes = useCallback(async (force = false): Promise<Dish[]> => {
    if (!force && isCacheValid(dishesCache)) {
      if (isMountedRef.current) {
        setDishes(dishesCache!.data);
      }
      return dishesCache!.data;
    }

    if (dishesRequest) {
      const sharedData = await dishesRequest;
      if (isMountedRef.current) {
        setDishes(sharedData);
      }
      return sharedData;
    }

    dishesRequest = dishService.getAllDishes();

    try {
      const fetchedDishes = await dishesRequest;
      dishesCache = {
        data: fetchedDishes,
        timestamp: Date.now(),
      };
      if (isMountedRef.current) {
        setDishes(fetchedDishes);
      }
      return fetchedDishes;
    } catch (error) {
      console.error('Error fetching dishes:', error);
      return [];
    } finally {
      dishesRequest = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    void Promise.all([fetchCustomizations(), fetchDishes()]);

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchCustomizations, fetchDishes]);

  const handleAddOrUpdateCustomization = async (values: Customization) => {
    try {
      if (editingCustomization) {
        await customizationService.updateCustomization(editingCustomization.id!, values);
        notification.success({
          message: 'Thành công',
          description: 'Customization đã được cập nhật thành công!',
        });
      } else {
        await customizationService.addCustomization(values);
        notification.success({
          message: 'Thành công',
          description: 'Customization mới đã được thêm thành công!',
        });
      }
      setModalVisible(false);
      form.resetFields();
      fetchCustomizations();
    } catch (error) {
      console.error('Error saving customization:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể lưu customization.',
      });
    }
  };

  const handleEdit = (customization: Customization) => {
    setEditingCustomization(customization);
    form.setFieldsValue(customization);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await customizationService.deleteCustomization(id);
      updateCustomizationsState((prev) => prev.filter((item) => item.id !== id));
      notification.success({
        message: 'Thành công',
        description: 'Customization đã được xóa thành công!',
      });
    } catch (error) {
      console.error('Error deleting customization:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa customization.',
      });
    }
  };

  const handleDeleteAll = async () => {
    if (customizations.length === 0) {
      notification.info({
        message: 'Không có dữ liệu',
        description: 'Hiện không có tùy chỉnh nào để xóa.',
      });
      return;
    }

    setDeletingAll(true);
    try {
      const targets = customizations.filter((item) => item.id);
      const results = await Promise.allSettled(
        targets.map((item) => customizationService.deleteCustomization(item.id!))
      );

      const deletedCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - deletedCount;

      if (deletedCount > 0) {
        const deletedIds = new Set(
          targets
            .filter((_, index) => results[index].status === 'fulfilled')
            .map((item) => item.id)
        );
        updateCustomizationsState((prev) => prev.filter((item) => !item.id || !deletedIds.has(item.id)));
      }

      if (failedCount === 0) {
        notification.success({
          message: 'Thành công',
          description: `Đã xóa ${deletedCount} tùy chỉnh.`,
        });
      } else {
        notification.warning({
          message: 'Xóa chưa hoàn tất',
          description: `Đã xóa ${deletedCount} tùy chỉnh, thất bại ${failedCount} tùy chỉnh.`,
        });
      }
    } catch (error) {
      console.error('Error deleting all customizations:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa tất cả tùy chỉnh.',
      });
    } finally {
      setDeletingAll(false);
    }
  };

  const handleAddNew = () => {
    setEditingCustomization(null);
    form.resetFields();
    form.setFieldsValue({ isRequired: true, limit: 1 });
    setModalVisible(true);
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1
    },
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Thành phố',
      dataIndex: 'city',
      key: 'city',
      width: 150,
    },
    {
      title: 'Mã món áp dụng',
      dataIndex: 'dishCode',
      key: 'dishCode',
      width: 300,
      render: (_: any, record: Customization) => (
        <Space wrap>
          {record.dishCode.map((code, index) => (
            <Tag key={index}>{code}</Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Nhóm tùy chọn',
      key: 'groups',
      width: 600,
      render: (_: any, record: Customization) => (
        <div>
          {record.groups.map((group, index) => (
            <div key={index} style={{ marginBottom: index < record.groups.length - 1 ? 8 : 0 }}>
              <div>
                <Tag color="purple">{group.groupName}</Tag>
                <Tag color={group.isRequired ? 'green' : 'red'}>
                  {group.isRequired ? 'Bắt buộc' : 'Không bắt buộc'}
                </Tag>
                <Tag>Giới hạn: {group.limit}</Tag>
              </div>
              <Space wrap style={{ marginTop: 4 }}>
                {group.dishCodes.map((dish, dishIndex) => (
                  <Tooltip key={dishIndex} title={`${dish.name} - ${dish.price.toLocaleString()}đ`}>
                    <Tag color="blue">{dish.code}</Tag>
                  </Tooltip>
                ))}
              </Space>
            </div>
          ))}
        </div>
      )
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: Customization) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Xóa customization"
            description="Bạn có chắc chắn muốn xóa customization này?"
            onConfirm={() => handleDelete(record.id!)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredCustomizations = useMemo(() => (
    searchTerm
      ? customizations.filter(customization =>
        customization.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customization.dishCode.some(code => code.toLowerCase().includes(searchTerm.toLowerCase()))
      )
      : customizations
  ), [customizations, searchTerm]);

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1 ref={titleRef}>Quản lý tùy chỉnh</h1>
          <Button
            type="primary"
            icon={<QuestionCircleOutlined />}
            onClick={() => setIsTourOpen(true)}
            style={{ marginLeft: 8 }}
          >
            Hướng dẫn
          </Button>
          <Tag color="blue">{customizations.length} tùy chỉnh</Tag>
        </div>

        <div className="admin-page-actions">
          <div className="admin-search-filters">
            <Search
              placeholder="Tìm kiếm theo tên hoặc mã món"
              allowClear
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 300 }}
              ref={searchRef}
            />
          </div>

          <Space>
            <Popconfirm
              title="Xóa toàn bộ tùy chỉnh"
              description="Bạn có chắc chắn muốn xóa tất cả tùy chỉnh? Hành động này không thể hoàn tác."
              onConfirm={handleDeleteAll}
              okText="Xóa hết"
              cancelText="Hủy"
              okButtonProps={{ danger: true, loading: deletingAll }}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={deletingAll}
                disabled={deletingAll || customizations.length === 0}
              >
                Xóa tất cả
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNew}
              className="admin-button"
              ref={addButtonRef}
            >
              Thêm tùy chỉnh mới
            </Button>
            <Tooltip title="Tải lên file Excel để cập nhật hoặc thêm mới customization">
              <Upload
                accept=".xlsx, .xls"
                showUploadList={false}
                beforeUpload={(file) => {
                  setLoading(true);
                  parseExcelToCustomizations(file).then((customizations) => {
                    customizationService.batchUpdateFromExcel(customizations).then((result) => {
                      setLoading(false);
                      notification.success({
                        message: 'Cập nhật thành công',
                        description: `Đã thêm ${result.added} customization mới, cập nhật ${result.updated} customization. ${result.errors.length > 0 ? `Có ${result.errors.length} lỗi.` : ''}`,
                      });

                      if (result.errors.length > 0) {
                        Modal.error({
                          title: 'Có lỗi xảy ra khi cập nhật',
                          content: (
                            <div>
                              <p>Các lỗi sau đã xảy ra:</p>
                              <ul>
                                {result.errors.map((error, index) => (
                                  <li key={index}>{error}</li>
                                ))}
                              </ul>
                            </div>
                          ),
                          width: 600,
                        });
                      }

                      void fetchCustomizations(true);
                    }).catch((error) => {
                      setLoading(false);
                      notification.error({
                        message: 'Lỗi',
                        description: `Không thể cập nhật danh sách customization: ${error.message}`,
                      });
                    });
                  }).catch((error) => {
                    setLoading(false);
                    notification.error({
                      message: 'Lỗi đọc file',
                      description: `Không thể đọc file Excel: ${error.message}`,
                    });
                  });
                  return false;
                }}
              >
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  className="admin-button"
                  ref={uploadButtonRef}
                >
                  Tải lên file Excel
                </Button>
              </Upload>
            </Tooltip>
          </Space>
        </div>
      </div>

      <div className="admin-form-section">
        <div className="admin-table-container">
          <Table
            className="admin-table"
            columns={columns}
            dataSource={filteredCustomizations}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1500 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Tổng số: ${total} tùy chỉnh`,
            }}
            ref={tableRef}
          />
        </div>
      </div>

      <Modal
        title={editingCustomization ? "Sửa thông tin tùy chỉnh" : "Thêm tùy chỉnh mới"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        className="admin-modal"
      >
        <div className="admin-form-container">
          <Form
            form={form}
            onFinish={handleAddOrUpdateCustomization}
            layout="vertical"
          >
            <div className="admin-form-section">
              <Form.Item
                name="name"
                label="Tên"
                rules={[{ required: true, message: 'Vui lòng nhập tên!' }]}
              >
                <Input placeholder="Nhập tên" />
              </Form.Item>

              <Form.Item
                name="city"
                label="Thành phố"
                rules={[{ required: true, message: 'Vui lòng chọn thành phố!' }]}
              >
                <Select placeholder="Chọn thành phố">
                  <Select.Option value="Hà Nội">Hà Nội</Select.Option>
                  <Select.Option value="Hồ Chí Minh">Hồ Chí Minh</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="dishCode"
                label="Mã món áp dụng"
                rules={[{ required: true, message: 'Vui lòng chọn mã món áp dụng!' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Chọn mã món áp dụng"
                  showSearch
                  optionFilterProp="children"
                >
                  {dishes.map(dish => (
                    <Select.Option key={dish.id} value={dish.code}>
                      {dish.code} - {dish.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="groupName"
                label="Tên nhóm"
                rules={[{ required: true, message: 'Vui lòng nhập tên nhóm!' }]}
              >
                <Input placeholder="Nhập tên nhóm" />
              </Form.Item>

              <Form.Item
                name="isRequired"
                label="Yêu cầu chọn"
                valuePropName="checked"
              >
                <Switch checkedChildren="Bắt buộc" unCheckedChildren="Không bắt buộc" />
              </Form.Item>

              <Form.Item
                name="limit"
                label="Giới hạn chọn"
                rules={[{ required: true, message: 'Vui lòng nhập giới hạn chọn!' }]}
              >
                <InputNumber style={{ width: '100%' }} min={1} placeholder="Nhập giới hạn chọn" />
              </Form.Item>

              <Form.Item
                name="dishCodes"
                label="Mã món theo nhóm"
                rules={[{ required: true, message: 'Vui lòng nhập mã món theo nhóm!' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Chọn mã món theo nhóm"
                  showSearch
                  optionFilterProp="children"
                >
                  {dishes.map(dish => (
                    <Select.Option key={dish.id} value={dish.code}>
                      {dish.code} - {dish.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="admin-button"
                >
                  {editingCustomization ? 'Cập nhật' : 'Thêm mới'}
                </Button>
              </Form.Item>
            </div>
          </Form>
        </div>
      </Modal>

      {/* Tour component */}
      <ConfigProvider locale={viVN}>
        <Tour
          open={isTourOpen}
          onClose={() => setIsTourOpen(false)}
          steps={tourSteps}
          indicatorsRender={(current, total) => (
            <span>{current + 1} / {total}</span>
          )}
          type="primary"
          arrow={true}
          placement="bottom"
          mask={{
            style: {
              boxShadow: 'inset 0 0 15px #333',
            },
          }}
        />
      </ConfigProvider>
    </AdminLayout>
  );
};

export default CustomizationManagement; 